import React, { useContext } from 'react';
import { Grid, Button, TextField } from '@material-ui/core';
import { Rating } from '@material-ui/lab';
import { css } from '@emotion/react';
import { colors } from '@/styles';
import { PrimaryButton, SecondaryButton } from '@/styles/blocks';
import { Context } from '../store';
import PhotoSection from './PhotoSection';
import { getLoadPhotosLabel, getLoadPhotosHint } from './constants';
import { useSurveyEditor } from './hooks';
import { responseValue } from './utils';

const readOnlyCss = (hasValue) => css`
    font-size: 14px;
    color: ${hasValue ? colors.greys[3] : colors.greys.secondary};
`;

const YesNoButton = ({ label, selected, color, onClick }) => (
    <Button
        size="small"
        variant={selected ? 'contained' : 'outlined'}
        onClick={onClick}
        css={css`
            min-width: 56px;
            ${selected &&
            css`
                background-color: ${color};
                color: white;
                &:hover {
                    background-color: ${color};
                }
            `}
        `}
    >
        {label}
    </Button>
);

const YesNo = ({ value, editable, onChange }) => {
    const answered = value === true || value === false;
    if (!editable)
        return <span css={readOnlyCss(answered)}>{value === true ? 'Yes' : value === false ? 'No' : '—'}</span>;
    return (
        <Grid
            container
            css={css`
                gap: 8px;
                width: auto;
            `}
        >
            <YesNoButton
                label="Yes"
                selected={value === true}
                color={colors.greens.primary}
                onClick={() => onChange(true)}
            />
            <YesNoButton
                label="No"
                selected={value === false}
                color={colors.reds.primary}
                onClick={() => onChange(false)}
            />
        </Grid>
    );
};

const TextAnswer = ({ value, editable, onChange }) => {
    if (!editable) return <span css={readOnlyCss(!!value)}>{value || '—'}</span>;
    return (
        <TextField
            size="small"
            variant="outlined"
            fullWidth
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
        />
    );
};

const NumberAnswer = ({ value, editable, onChange }) => (
    <Rating readOnly={!editable} size="large" precision={0.5} value={value || 0} onChange={(e, val) => onChange(val)} />
);

const ANSWER_COMPONENTS = { boolean: YesNo, number: NumberAnswer, string: TextAnswer };

// One question stacked over its answer.
const QuestionRow = ({ label, responseType, value, editable, onChange }) => {
    const Answer = ANSWER_COMPONENTS[responseType] || YesNo;
    return (
        <Grid
            container
            direction="column"
            alignItems="flex-start"
            css={css`
                gap: 0.5rem;
            `}
        >
            <span
                css={css`
                    font-size: 14px;
                    font-weight: 700;
                `}
            >
                {label}
            </span>
            <Answer value={value} editable={editable} onChange={onChange} />
        </Grid>
    );
};

const QuestionsHeader = ({ canEdit, editor, saving }) => (
    <Grid
        container
        justifyContent="space-between"
        alignItems="center"
        css={css`
            margin: 1rem 0;
        `}
    >
        <span
            css={css`
                font-size: 16px;
                font-weight: 800;
            `}
        >
            Questions
        </span>
        {canEdit &&
            (editor.isEditing ? (
                <Grid
                    container
                    css={css`
                        gap: 8px;
                        width: auto;
                    `}
                >
                    <SecondaryButton onClick={editor.cancel} disabled={saving}>
                        Cancel
                    </SecondaryButton>
                    <PrimaryButton onClick={editor.save} loading={saving}>
                        Save
                    </PrimaryButton>
                </Grid>
            ) : (
                <SecondaryButton onClick={editor.startEditing}>Edit</SecondaryButton>
            ))}
    </Grid>
);

const Survey = () => {
    const { state, loading, callbacks } = useContext(Context);
    const manifest = state.manifest || {};
    const editable = state.editable;
    const type = manifest.type;
    const questions = state.surveyQuestions || [];
    const responses = state.surveyResponses || {};
    const editor = useSurveyEditor({ saveSurveyResponses: callbacks.saveSurveyResponses });

    // Photos save on every add/delete; answers save on the Save button.
    const persist = (patch) => {
        if (!manifest.manifest_id) return;
        callbacks.saveManifest({ manifest_id: manifest.manifest_id, ...patch });
    };

    const onUploadError = (err) => {
        console.error(err);
        callbacks.setNotification({ severity: 'error', message: 'Failed to upload photo' });
    };

    return (
        <Grid
            container
            direction="column"
            css={css`
                padding: 2rem max(12.5%, 6rem);
            `}
        >
            <div
                css={css`
                    background-color: white;
                    border: 1px solid ${colors.greys.border};
                    border-radius: 8px;
                    box-shadow: 0px 1px 3px 0px #3f3f4426;
                    padding: 1.5rem 2rem;
                `}
            >
                <div
                    css={css`
                        font-size: 16px;
                        font-weight: 800;
                        margin-bottom: 1.5rem;
                    `}
                >
                    Load Feedback Survey
                </div>

                <PhotoSection
                    title={getLoadPhotosLabel(type)}
                    hint={getLoadPhotosHint(type)}
                    photos={manifest.load_photos}
                    editable={editable}
                    folder="load"
                    category="load_photos"
                    onChange={(next) => persist({ load_photos: next })}
                    onError={onUploadError}
                />

                <PhotoSection
                    title="Signed BOL"
                    photos={manifest.bol_photos}
                    editable={editable}
                    folder="bol"
                    category="bol_photos"
                    onChange={(next) => persist({ bol_photos: next })}
                    onError={onUploadError}
                />

                {questions.length > 0 && (
                    <>
                        <QuestionsHeader canEdit={editable} editor={editor} saving={loading?.surveySaveLoading} />
                        <Grid
                            container
                            direction="column"
                            css={css`
                                gap: 1.25rem;
                            `}
                        >
                            {questions.map((q, index) => {
                                const saved = responseValue(q.response_type, responses[q.survey_id]);
                                return (
                                    <QuestionRow
                                        key={q.survey_id}
                                        label={`${index + 1}. ${q.question}`}
                                        responseType={q.response_type}
                                        value={q.survey_id in editor.draft ? editor.draft[q.survey_id] : saved}
                                        editable={editable && editor.isEditing}
                                        onChange={(val) => editor.setAnswer(q.survey_id, val)}
                                    />
                                );
                            })}
                        </Grid>
                    </>
                )}
            </div>
        </Grid>
    );
};

export default Survey;
